import { useState, Fragment } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { downloadReport } from "@/lib/api";
import { PageTitle } from "@/components/PageTitle";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { DatePicker } from "@/components/DatePicker";
import { SearchSelect } from "@/components/SearchSelect";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Users, ShoppingCart, Wallet, Scale, Receipt, UserCog, BadgeIndianRupee, Package, FileDown, FileSpreadsheet, Eye,
} from "lucide-react";
import { toast } from "sonner";

const REPORTS = [
  { key: "suppliers", label: "Supplier Report", icon: Users, path: "/reports/suppliers/export", filters: ["category"] },
  { key: "purchases", label: "Purchase Report", icon: ShoppingCart, path: "/reports/purchases/export", filters: ["supplier", "status", "dates"] },
  { key: "payments", label: "Payment Report", icon: Wallet, path: "/reports/payments/export", filters: ["supplier", "dates"] },
  { key: "balance", label: "Balance Report", icon: Scale, path: "/reports/balance/export", filters: ["supplier", "category", "dates"] },
  { key: "consolidated", label: "Consolidated Balance Sheet", icon: BadgeIndianRupee, path: "/reports/consolidated/export", filters: [] },
  { key: "products", label: "Product Purchase Summary", icon: Package, path: "/reports/products/export", filters: ["dates"] },
  { key: "expenses", label: "Expense Report", icon: Receipt, path: "/reports/expenses/export", filters: ["dates"] },
  { key: "employees", label: "Employee Report", icon: UserCog, path: "/reports/employees/export", filters: [] },
  { key: "salaries", label: "Salary Report", icon: BadgeIndianRupee, path: "/reports/salaries/export", filters: ["employee", "month"] },
];

export default function Reports() {
  const [active, setActive] = useState(REPORTS[0]);
  const [f, setF] = useState({ category: "all", status: "all", supplier: "all", employee: "all", date_from: "", date_to: "", month: "" });
  const [result, setResult] = useState(null);
  const [viewing, setViewing] = useState(false);

  const { data: suppliers = [] } = useQuery({ queryKey: ["suppliers-min"], queryFn: async () => (await api.get("/suppliers")).data });
  const { data: cats = [] } = useQuery({ queryKey: ["categories"], queryFn: async () => (await api.get("/suppliers/categories")).data });
  const { data: employees = [] } = useQuery({ queryKey: ["employees"], queryFn: async () => (await api.get("/employees")).data });

  const buildParams = (fmt) => {
    const p = { fmt };
    if (active.filters.includes("category") && f.category !== "all") p.category = f.category;
    if (active.filters.includes("status") && f.status !== "all") p.status = f.status;
    if (active.filters.includes("supplier") && f.supplier !== "all") p.supplier_id = f.supplier;
    if (active.filters.includes("employee") && f.employee !== "all") p.employee_id = f.employee;
    if (active.filters.includes("dates")) { if (f.date_from) p.date_from = f.date_from; if (f.date_to) p.date_to = f.date_to; }
    if (active.filters.includes("month") && f.month) p.month = f.month;
    return p;
  };

  const run = async (fmt) => {
    try {
      await downloadReport(active.path, buildParams(fmt), active.key);
      toast.success(`${active.label} (${fmt.toUpperCase()}) downloaded`);
    } catch {
      toast.error("Export failed");
    }
  };

  const view = async () => {
    setViewing(true);
    try {
      const p = buildParams("pdf"); delete p.fmt;
      const { data } = await api.get("/reports/view", { params: { kind: active.key, ...p } });
      setResult(data);
    } catch {
      toast.error("Failed to load report");
    } finally {
      setViewing(false);
    }
  };

  return (
    <div>
      <PageTitle title="Reports" subtitle="Generate & export PDF / Excel reports with filters" />

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-1 gap-2 lg:col-span-1">
          {REPORTS.map((r) => (
            <button
              key={r.key}
              onClick={() => { setActive(r); setResult(null); }}
              data-testid={`report-tab-${r.key}`}
              className={`flex items-center gap-3 rounded-lg border px-4 py-3 text-sm font-medium text-left transition-colors ${active.key === r.key ? "border-primary bg-primary/5 text-primary" : "border-border hover:bg-muted"}`}
            >
              <r.icon className="h-4 w-4" /> {r.label}
            </button>
          ))}
        </div>

        <Card className="lg:col-span-2 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="grid h-11 w-11 place-items-center rounded-xl bg-primary/10 text-primary"><active.icon className="h-5 w-5" /></div>
            <div><h3 className="font-display text-lg font-bold">{active.label}</h3><p className="text-sm text-muted-foreground">Apply filters, then export.</p></div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            {active.filters.includes("supplier") && (
              <div><Label>Supplier</Label>
                <div className="mt-1.5">
                  <SearchSelect value={f.supplier} onChange={(v) => setF((s) => ({ ...s, supplier: v }))}
                    options={[{ value: "all", label: "All" }, ...suppliers.map((s) => ({ value: s.id, label: `${s.supplier_code} — ${s.name}` }))]}
                    searchPlaceholder="Type supplier name or code…" testid="rf-supplier" />
                </div>
              </div>
            )}
            {active.filters.includes("category") && (
              <div><Label>Category</Label>
                <div className="mt-1.5">
                  <SearchSelect value={f.category} onChange={(v) => setF((s) => ({ ...s, category: v }))}
                    options={[{ value: "all", label: "All" }, ...cats.map((c) => ({ value: c, label: c }))]}
                    searchPlaceholder="Type category…" testid="rf-category" />
                </div>
              </div>
            )}
            {active.filters.includes("status") && (
              <div><Label>Status</Label>
                <Select value={f.status} onValueChange={(v) => setF((s) => ({ ...s, status: v }))}>
                  <SelectTrigger className="mt-1.5" data-testid="rf-status"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="unpaid">Unpaid</SelectItem><SelectItem value="partial">Partial</SelectItem><SelectItem value="paid">Paid</SelectItem></SelectContent>
                </Select>
              </div>
            )}
            {active.filters.includes("employee") && (
              <div><Label>Employee</Label>
                <div className="mt-1.5">
                  <SearchSelect value={f.employee} onChange={(v) => setF((s) => ({ ...s, employee: v }))}
                    options={[{ value: "all", label: "All" }, ...employees.map((e) => ({ value: e.id, label: `${e.employee_no} — ${e.name}` }))]}
                    searchPlaceholder="Type employee name or code…" testid="rf-employee" />
                </div>
              </div>
            )}
            {active.filters.includes("month") && (
              <div><Label>Month</Label><Input type="month" value={f.month} onChange={(e) => setF((s) => ({ ...s, month: e.target.value }))} className="mt-1.5" data-testid="rf-month" /></div>
            )}
            {active.filters.includes("dates") && (
              <>
                <div><Label>From</Label><div className="mt-1.5"><DatePicker value={f.date_from} onChange={(v) => setF((s) => ({ ...s, date_from: v }))} testid="rf-from" /></div></div>
                <div><Label>To</Label><div className="mt-1.5"><DatePicker value={f.date_to} onChange={(v) => setF((s) => ({ ...s, date_to: v }))} testid="rf-to" /></div></div>
              </>
            )}
            {active.filters.length === 0 && <p className="text-sm text-muted-foreground sm:col-span-2">No filters — exports all records.</p>}
          </div>

          <div className="flex flex-wrap gap-3 mt-8">
            <Button onClick={view} disabled={viewing} className="flex-1 min-w-[120px]" data-testid="run-view"><Eye className="h-4 w-4" /> {viewing ? "Loading…" : "View"}</Button>
            <Button onClick={() => run("pdf")} variant="outline" className="flex-1 min-w-[120px]" data-testid="run-pdf"><FileDown className="h-4 w-4" /> Export PDF</Button>
            <Button onClick={() => run("excel")} variant="outline" className="flex-1 min-w-[120px]" data-testid="run-excel"><FileSpreadsheet className="h-4 w-4" /> Export Excel</Button>
          </div>
        </Card>
      </div>

      {result && (
        <Card className="mt-4 overflow-hidden animate-fade-up" data-testid="report-result">
          <div className="p-4 border-b flex items-center justify-between gap-3">
            <div><h3 className="font-display font-bold">{result.title}</h3>{result.summary && <p className="text-xs text-muted-foreground mt-0.5">{result.summary}</p>}</div>
            <span className="text-sm text-muted-foreground whitespace-nowrap">{result.rows.length} rows</span>
          </div>
          <div className="overflow-x-auto ssacc-scroll">
            <Table>
              <TableHeader><TableRow className="bg-muted/50">{result.columns.filter((c) => !(active.key === "purchases" && c === "Product Details")).map((c) => <TableHead key={c}>{c}</TableHead>)}</TableRow></TableHeader>
              <TableBody>
                {result.rows.length === 0 ? (
                  <TableRow><TableCell colSpan={result.columns.length} className="text-center py-8 text-muted-foreground">No records.</TableCell></TableRow>
                ) : result.rows.map((row, i) => {
                  const cols = result.columns.filter((c) => !(active.key === "purchases" && c === "Product Details"));
                  const label = String(row["Supplier"] || row["Supplier Code"] || row["Product"] || "");
                  const isGrand = Object.values(row).some((v) => typeof v === "string" && (v.includes("GRAND TOTAL") || v.includes("TOTAL OUTSTANDING")));
                  const isSub = !isGrand && label.includes("Subtotal");
                  const rowCls = isGrand ? "bg-primary/10 font-bold" : isSub ? "bg-muted/60 font-semibold" : "";
                  return (
                    <Fragment key={i}>
                      <TableRow data-testid={isGrand ? "report-grand-total-row" : isSub ? "report-subtotal-row" : "report-result-row"} className={rowCls}>{cols.map((c) => <TableCell key={c} className="data-num align-top whitespace-pre-line">{String(row[c])}</TableCell>)}</TableRow>
                      {active.key === "purchases" && row["Product Details"] ? (
                        <TableRow data-testid="report-detail-row">
                          <TableCell colSpan={cols.length} className="bg-muted/30 text-sm text-muted-foreground whitespace-pre-line align-top py-2.5 px-6">{String(row["Product Details"])}</TableCell>
                        </TableRow>
                      ) : null}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
}
